import { useCallback, useState } from 'react';
import { FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { Checkbox, EmptyPlaceholder, Skeleton, Text } from '@client/toolkit';
import { SpaceHierarchyReadModel, useGetSpacesByIdHierarchyQuery } from '@client/api';
import classNames from 'classnames';

interface SpaceHierarchyTreeProps {
  buildingId: string;
  selectedSpaces: SpaceHierarchyReadModel[];
  onToggleSpace: (space: SpaceHierarchyReadModel, isSelected: boolean) => void;
  searchQuery?: string;
}

export function SpaceHierarchyTree({
  buildingId,
  selectedSpaces,
  onToggleSpace,
  searchQuery,
}: SpaceHierarchyTreeProps) {
  const { data: hierarchyData, isLoading } = useGetSpacesByIdHierarchyQuery({ id: buildingId }, { skip: !buildingId });

  const isSpaceSelected = useCallback(
    (spaceId: string) => selectedSpaces.some((s) => s.id === spaceId),
    [selectedSpaces],
  );

  const filterBySearch = useCallback(
    (space: SpaceHierarchyReadModel): boolean => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();

      if (space.name?.toLowerCase().includes(query)) return true;

      if (space.children && space.children.length > 0) {
        return space.children.some((child) => filterBySearch(child));
      }

      return false;
    },
    [searchQuery],
  );

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-8 w-full" />
        ))}
      </div>
    );
  }

  if (!hierarchyData) {
    return <EmptyPlaceholder>No hierarchy data available</EmptyPlaceholder>;
  }

  return (
    <div className="flex flex-col gap-1">
      <SpaceTreeNode
        space={hierarchyData}
        depth={0}
        isSelected={isSpaceSelected(hierarchyData.id)}
        onToggle={(isSelected) => onToggleSpace(hierarchyData, isSelected)}
        selectedSpaces={selectedSpaces}
        onToggleSpace={onToggleSpace}
        isSpaceSelected={isSpaceSelected}
        filterBySearch={filterBySearch}
        ancestors={[]}
      />
    </div>
  );
}

interface SpaceTreeNodeProps {
  space: SpaceHierarchyReadModel;
  depth: number;
  isSelected: boolean;
  onToggle: (isSelected: boolean) => void;
  selectedSpaces: SpaceHierarchyReadModel[];
  onToggleSpace: (space: SpaceHierarchyReadModel, isSelected: boolean) => void;
  isSpaceSelected: (spaceId: string) => boolean;
  filterBySearch: (space: SpaceHierarchyReadModel) => boolean;
  ancestors?: SpaceHierarchyReadModel[];
}

function SpaceTreeNode({
  space,
  depth,
  isSelected,
  onToggle,
  selectedSpaces,
  onToggleSpace,
  isSpaceSelected,
  filterBySearch,
  ancestors = [],
}: SpaceTreeNodeProps) {
  const [isOpen, setIsOpen] = useState(depth === 0);

  const hasChildren = space.children && space.children.length > 0;
  const shouldShow = filterBySearch(space);

  if (!shouldShow) return null;

  const handleToggle = () => {
    const spaceWithPath = {
      ...space,
      displayPath: ancestors.length > 0 ? [...ancestors, space].map((s) => s.name).join(' - ') : space.name,
    } as SpaceHierarchyReadModel & { displayPath?: string };

    onToggleSpace(spaceWithPath, !isSelected);
  };

  const currentAncestors = [...ancestors, space];

  return (
    <div>
      <div
        className={classNames(
          'flex items-center gap-2 py-1.5 px-2 rounded hover:bg-white/5 cursor-pointer transition-colors',
          {
            'bg-white/10': isSelected,
          },
        )}
        style={{ paddingLeft: `${depth * 20 + 8}px` }}
      >
        {hasChildren && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(!isOpen);
            }}
            className="text-white/60 hover:text-white transition-colors"
          >
            {isOpen ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />}
          </button>
        )}
        {!hasChildren && <div className="w-3" />}

        <Checkbox checked={isSelected} onChange={handleToggle} onClick={(e) => e.stopPropagation()} />

        <div className="flex-1 cursor-pointer" onClick={handleToggle}>
          <Text className="text-sm">{space.name}</Text>
        </div>
      </div>

      {hasChildren && isOpen && (
        <div>
          {space.children!.map((child) => (
            <SpaceTreeNode
              key={child.id}
              space={child}
              depth={depth + 1}
              isSelected={isSpaceSelected(child.id)}
              onToggle={(isSelected) => {
                const childWithPath = {
                  ...child,
                  displayPath: [...currentAncestors, child].map((s) => s.name).join(' - '),
                } as SpaceHierarchyReadModel & { displayPath?: string };
                onToggleSpace(childWithPath, isSelected);
              }}
              selectedSpaces={selectedSpaces}
              onToggleSpace={onToggleSpace}
              isSpaceSelected={isSpaceSelected}
              filterBySearch={filterBySearch}
              ancestors={currentAncestors}
            />
          ))}
        </div>
      )}
    </div>
  );
}
